/**
 * Notification Settings Component
 * 
 * Allows users to configure push notification and background tracking preferences
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Bell, MapPin, Shield, Users, AlertTriangle, Navigation, BatteryCharging } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { isNative, LocationService } from '@/lib/capacitor';
import { BackgroundLocationService, hasBackgroundLocationConsent, setBackgroundLocationConsent } from '@/lib/backgroundLocation';
import { PushNotificationService } from '@/lib/pushNotifications';
import BackgroundLocationDisclosure from './BackgroundLocationDisclosure';

const NotificationSettings = () => {
  const { user, updateUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Background tracking settings
  const [allowBackgroundTracking, setAllowBackgroundTracking] = useState(false);
  const [backgroundPermissionGranted, setBackgroundPermissionGranted] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  // "Always share my location" - persistent sharing independent of trips
  const [alwaysShare, setAlwaysShare] = useState(false);
  const [showAlwaysDisclosure, setShowAlwaysDisclosure] = useState(false);
  const [alwaysSaving, setAlwaysSaving] = useState(false);
  
  // Push notification settings
  const [pushEnabled, setPushEnabled] = useState(false);
  const [preferences, setPreferences] = useState({
    sos_alerts: true,
    friend_overdue: true,
    friend_started_trip: false,
    friend_ended_trip: false,
  });

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Keep the "Always share" toggle in sync with the user profile
  useEffect(() => {
    setAlwaysShare(!!user?.always_share_location);
  }, [user?.always_share_location]);

  const loadSettings = async () => {
    // Always ensure we exit loading state
    try {
      // Load notification preferences from API
      try {
        const response = await api.get('/notifications/preferences');
        if (response.data) {
          setPreferences(response.data);
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        // Continue with defaults - don't block the UI
      }
      
      // Check local storage for background tracking opt-in
      try {
        const bgTracking = localStorage.getItem('boat360_bg_tracking');
        setAllowBackgroundTracking(bgTracking === 'true');
      } catch (e) {
        console.error('Failed to read localStorage:', e);
      }
      
      // Check push notification status (with timeout to prevent hanging)
      try {
        if (PushNotificationService && typeof PushNotificationService.checkPermissions === 'function') {
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 3000)
          );
          const pushStatus = await Promise.race([
            PushNotificationService.checkPermissions(),
            timeoutPromise
          ]).catch(() => ({ receive: 'denied' }));
          setPushEnabled(pushStatus?.receive === 'granted');
        }
      } catch (error) {
        console.error('Failed to check push permissions:', error);
      }
      
      // Check background location permission (only on native, with timeout)
      try {
        if (isNative && BackgroundLocationService && typeof BackgroundLocationService.checkPermissions === 'function') {
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 3000)
          );
          const bgPermission = await Promise.race([
            BackgroundLocationService.checkPermissions(),
            timeoutPromise
          ]).catch(() => ({ granted: false }));
          setBackgroundPermissionGranted(bgPermission?.granted || false);
        }
      } catch (error) {
        console.error('Failed to check background location permissions:', error);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      // Always set loading to false
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      setSaving(true);
      await api.put('/notifications/preferences', newPrefs);
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
      // Revert
      setPreferences(preferences);
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundTrackingChange = async (enabled) => {
    if (!enabled) {
      // Turning off is always immediate
      setAllowBackgroundTracking(false);
      localStorage.setItem('boat360_bg_tracking', 'false');
      setBackgroundLocationConsent(false);
      toast.success('Background tracking disabled');
      return;
    }

    // Turning ON: show the prominent disclosure BEFORE requesting any permission.
    // Required by Google Play / Apple before ACCESS_BACKGROUND_LOCATION is requested.
    setShowDisclosure(true);
  };

  // User tapped "Allow" on the prominent disclosure -> now we may request the OS permission
  const handleDisclosureAllow = async () => {
    setShowDisclosure(false);
    setBackgroundLocationConsent(true);
    setAllowBackgroundTracking(true);
    localStorage.setItem('boat360_bg_tracking', 'true');

    try {
      // Show the native OS location runtime permission dialog right after the
      // disclosure (Google requires the device permission prompt to be visible).
      await LocationService.requestNativeLocationPermission();
      const permission = await BackgroundLocationService.requestPermissions();
      if (permission.granted) {
        setBackgroundPermissionGranted(true);
        toast.success('Background tracking enabled');
      } else {
        toast.message('Choose "Allow all the time" in system settings so tracking works while the app is closed.');
      }
    } catch (e) {
      toast.success('Background tracking enabled');
    }
  };

  // User declined the disclosure -> do not request permission, keep toggle off
  const handleDisclosureDecline = () => {
    setShowDisclosure(false);
    setBackgroundLocationConsent(false);
    setAllowBackgroundTracking(false);
    localStorage.setItem('boat360_bg_tracking', 'false');
  };

  // ---- "Always share my location" (persistent, independent of trips) ----
  const handleAlwaysShareChange = async (enabled) => {
    if (!enabled) {
      // Turning off is immediate
      setAlwaysShare(false);
      setAlwaysSaving(true);
      try {
        await updateUser({ always_share_location: false });
        toast.success('Always-on location sharing disabled');
      } catch (e) {
        setAlwaysShare(true);
        toast.error('Could not update setting');
      } finally {
        setAlwaysSaving(false);
      }
      return;
    }
    // Turning ON requires the prominent disclosure before requesting permission
    setShowAlwaysDisclosure(true);
  };

  const handleAlwaysDisclosureAllow = async () => {
    setShowAlwaysDisclosure(false);
    // Consent must be set before enabling so the background watcher can start
    setBackgroundLocationConsent(true);

    if (isNative) {
      try {
        await LocationService.requestNativeLocationPermission();
        const permission = await BackgroundLocationService.requestPermissions();
        if (permission.granted) {
          setBackgroundPermissionGranted(true);
        } else {
          toast.message('Choose "Allow all the time" in system settings so sharing works while the app is closed.');
        }
      } catch (e) {
        /* proceed even if permission prompt fails; user can adjust in settings */
      }
    }

    setAlwaysSaving(true);
    try {
      await updateUser({ always_share_location: true });
      setAlwaysShare(true);
      toast.success('Always-on location sharing enabled');
    } catch (e) {
      setBackgroundLocationConsent(false);
      toast.error('Could not update setting');
    } finally {
      setAlwaysSaving(false);
    }
  };

  const handleAlwaysDisclosureDecline = () => {
    setShowAlwaysDisclosure(false);
    setAlwaysShare(false);
  };

  const handleEnablePush = async () => {
    try {
      const token = await PushNotificationService.register();
      if (token) {
        setPushEnabled(true);
        toast.success('Push notifications enabled');
      } else {
        toast.error('Could not enable push notifications');
      }
    } catch (error) {
      toast.error('Failed to enable push notifications');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Prominent disclosure shown before requesting background location permission */}
      <BackgroundLocationDisclosure
        isOpen={showDisclosure}
        onAllow={handleDisclosureAllow}
        onDecline={handleDisclosureDecline}
      />
      {/* Prominent disclosure for persistent "Always share" location */}
      <BackgroundLocationDisclosure
        isOpen={showAlwaysDisclosure}
        onAllow={handleAlwaysDisclosureAllow}
        onDecline={handleAlwaysDisclosureDecline}
      />
      {/* Background Tracking */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Navigation className="w-5 h-5 text-cyan-600" />
            Location Tracking
          </CardTitle>
          <CardDescription className="text-gray-500">
            Trip tracking and always-on location sharing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="bg-tracking" className="text-base">
                Allow Background Tracking
              </Label>
              <p className="text-sm text-gray-500">
                Track your trip even when the app is in the background
              </p>
            </div>
            <Switch
              id="bg-tracking"
              checked={allowBackgroundTracking}
              onCheckedChange={handleBackgroundTrackingChange}
              data-testid="bg-tracking-switch"
            />
          </div>
          
          {allowBackgroundTracking && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-700">
                <Shield className="w-3 h-3 inline mr-1" />
                Background tracking only runs during active trips. It automatically stops when you end your trip.
              </p>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 pr-3">
                <Label htmlFor="always-share" className="text-base">
                  Always Share My Location
                </Label>
                <p className="text-sm text-gray-500">
                  Keep sharing your location with your crew even when your phone is locked and no trip is active.
                </p>
              </div>
              <Switch
                id="always-share"
                checked={alwaysShare}
                disabled={alwaysSaving}
                onCheckedChange={handleAlwaysShareChange}
                data-testid="always-share-switch"
              />
            </div>

            {alwaysShare && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3" data-testid="always-share-battery-note">
                <p className="text-xs text-amber-700">
                  <BatteryCharging className="w-3 h-3 inline mr-1" />
                  This keeps GPS active in the background and uses more battery. Turn it off when you don&apos;t need to be visible.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-600" />
            Push Notifications
          </CardTitle>
          <CardDescription className="text-gray-500">
            Choose which alerts you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pushEnabled && isNative && (
            <Button 
              onClick={handleEnablePush}
              className="w-full bg-cyan-500 hover:bg-cyan-600"
              data-testid="enable-push-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Enable Push Notifications
            </Button>
          )}

          {(pushEnabled || !isNative) && (
            <>
              {/* SOS Alerts */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <div>
                    <Label htmlFor="sos-alerts" className="text-base">SOS Alerts</Label>
                    <p className="text-sm text-gray-500">When a friend sends an SOS</p>
                  </div>
                </div>
                <Switch
                  id="sos-alerts"
                  checked={preferences.sos_alerts}
                  onCheckedChange={(v) => handlePreferenceChange('sos_alerts', v)}
                  disabled={saving}
                />
              </div>

              {/* Friend Overdue */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <div>
                    <Label htmlFor="friend-overdue" className="text-base">Overdue Alerts</Label>
                    <p className="text-sm text-gray-500">When a friend doesn&apos;t return on time</p>
                  </div>
                </div>
                <Switch
                  id="friend-overdue"
                  checked={preferences.friend_overdue}
                  onCheckedChange={(v) => handlePreferenceChange('friend_overdue', v)}
                  disabled={saving}
                />
              </div>

              {/* Friend Started Trip */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <div>
                    <Label htmlFor="friend-started" className="text-base">Trip Started</Label>
                    <p className="text-sm text-gray-500">When a friend starts a trip</p>
                  </div>
                </div>
                <Switch
                  id="friend-started"
                  checked={preferences.friend_started_trip}
                  onCheckedChange={(v) => handlePreferenceChange('friend_started_trip', v)}
                  disabled={saving}
                />
              </div>

              {/* Friend Ended Trip */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-500" />
                  <div>
                    <Label htmlFor="friend-ended" className="text-base">Trip Ended</Label>
                    <p className="text-sm text-gray-500">When a friend ends their trip</p>
                  </div>
                </div>
                <Switch
                  id="friend-ended"
                  checked={preferences.friend_ended_trip}
                  onCheckedChange={(v) => handlePreferenceChange('friend_ended_trip', v)}
                  disabled={saving}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
